/**
 * Main WebRTC Hook
 * Orchestrates signaling, media streams, and peer connections
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useWebRTCSignaling } from './useWebRTCSignaling';
import { useMediaStream } from './useMediaStream';
import { usePeerConnection } from './usePeerConnection';
import type {
  UseWebRTCOptions,
  UseWebRTCReturn,
  PeerInfo,
} from '@/api/types/webrtc.types';

/**
 * Main WebRTC hook - coordinates all WebRTC functionality
 *
 * @example
 * ```typescript
 * const {
 *   localStream,
 *   peers,
 *   isConnected,
 *   joinRoom,
 *   leaveRoom,
 *   toggleAudio,
 *   toggleVideo,
 *   startScreenShare
 * } = useWebRTC({
 *   roomName: `${tenantId}-meeting-${meetingId}`,
 *   userId: currentUser.id,
 *   displayName: currentUser.displayName,
 *   tenantId: currentUser.tenant_id
 * });
 * ```
 */
export function useWebRTC({
  roomName,
  userId,
  displayName,
  tenantId,
  autoJoin = false,
  iceServers = [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
  ],
}: UseWebRTCOptions): UseWebRTCReturn {
  // State
  const [peers, setPeers] = useState<Map<string, PeerInfo>>(new Map());
  const [isJoined, setIsJoined] = useState(false);
  const [connectionState, setConnectionState] = useState<
    'idle' | 'connecting' | 'connected' | 'disconnected' | 'failed'
  >('idle');
  const [error, setError] = useState<Error | null>(null);

  // Refs to manage peer connections
  const peerConnectionsRef = useRef<
    Map<
      string,
      {
        connection: RTCPeerConnection;
        createOffer: () => Promise<RTCSessionDescriptionInit | null>;
        createAnswer: () => Promise<RTCSessionDescriptionInit | null>;
        setRemoteDescription: (sdp: RTCSessionDescriptionInit) => Promise<void>;
        addIceCandidate: (candidate: RTCIceCandidateInit) => Promise<void>;
        close: () => void;
      }
    >
  >(new Map());

  // Media stream hook
  const {
    stream: localStream,
    isAudioEnabled,
    isVideoEnabled,
    isScreenSharing,
    toggleAudio,
    toggleVideo,
    startScreenShare: startScreenShareMedia,
    stopScreenShare: stopScreenShareMedia,
    startMedia,
    error: mediaError,
  } = useMediaStream({
    audio: true,
    video: true,
    autoStart: true,
  });

  // Signaling hook
  const {
    isConnected: signalingConnected,
    joinRoom: joinSignalingRoom,
    leaveRoom: leaveSignalingRoom,
    sendOffer,
    sendAnswer,
    sendIceCandidate,
  } = useWebRTCSignaling({
    roomName,
    userId,
    displayName,
    enabled: true,

    // Peer joined callback
    onPeerJoined: async (peerId, peerDisplayName) => {
      console.log(`👤 Peer joined: ${peerDisplayName} (${peerId})`);

      // Create peer connection using hook-like pattern
      const pc = new RTCPeerConnection({ iceServers });

      // Handle ICE candidates
      pc.onicecandidate = (event) => {
        if (event.candidate) {
          sendIceCandidate(event.candidate);
        }
      };

      // Handle remote track
      pc.ontrack = (event) => {
        console.log(`📹 Received remote track from ${peerId}`);
        const stream = event.streams[0];

        setPeers((prev) => {
          const next = new Map(prev);
          const existingPeer = next.get(peerId) || {
            id: peerId,
            displayName: peerDisplayName,
            stream: null,
            connectionState: 'new' as RTCPeerConnectionState,
          };
          next.set(peerId, { ...existingPeer, stream });
          return next;
        });
      };

      // Handle connection state changes
      pc.onconnectionstatechange = () => {
        console.log(`🔌 Connection state for ${peerId}:`, pc.connectionState);

        setPeers((prev) => {
          const next = new Map(prev);
          const existingPeer = next.get(peerId);
          if (existingPeer) {
            next.set(peerId, {
              ...existingPeer,
              connectionState: pc.connectionState,
            });
          }
          return next;
        });

        if (pc.connectionState === 'connected') {
          setConnectionState('connected');
        } else if (
          pc.connectionState === 'failed' ||
          pc.connectionState === 'disconnected'
        ) {
          setConnectionState('failed');
        }
      };

      // Handle ICE connection state
      pc.oniceconnectionstatechange = () => {
        if (pc.iceConnectionState === 'failed') {
          console.warn(`⚠️ ICE failed for ${peerId}, restarting...`);
          pc.restartIce();
        }
      };

      // Add local stream tracks
      if (localStream) {
        localStream.getTracks().forEach((track) => {
          pc.addTrack(track, localStream);
        });
      }

      // Store peer connection
      peerConnectionsRef.current.set(peerId, {
        connection: pc,
        createOffer: async () => {
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          return pc.localDescription;
        },
        createAnswer: async () => {
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          return pc.localDescription;
        },
        setRemoteDescription: async (sdp) => {
          await pc.setRemoteDescription(new RTCSessionDescription(sdp));
        },
        addIceCandidate: async (candidate) => {
          await pc.addIceCandidate(new RTCIceCandidate(candidate));
        },
        close: () => {
          pc.close();
        },
      });

      // Add peer to state
      setPeers((prev) => {
        const next = new Map(prev);
        next.set(peerId, {
          id: peerId,
          displayName: peerDisplayName,
          stream: null,
          connectionState: 'new',
        });
        return next;
      });

      // Create and send offer
      try {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        sendOffer(pc.localDescription!);
        console.log(`📤 Sent offer to ${peerId}`);
      } catch (err) {
        console.error(`❌ Failed to create offer for ${peerId}:`, err);
      }
    },

    // Peer left callback
    onPeerLeft: (peerId) => {
      console.log(`👋 Peer left: ${peerId}`);

      // Close and remove peer connection
      const peerConn = peerConnectionsRef.current.get(peerId);
      if (peerConn) {
        peerConn.close();
        peerConnectionsRef.current.delete(peerId);
      }

      // Remove from state
      setPeers((prev) => {
        const next = new Map(prev);
        next.delete(peerId);
        return next;
      });
    },

    // Received offer
    onOffer: async (sdp, fromUserId) => {
      console.log(`📩 Received offer from ${fromUserId}`);

      const peerConn = peerConnectionsRef.current.get(fromUserId);
      if (!peerConn) {
        console.error(`❌ No peer connection found for ${fromUserId}`);
        return;
      }

      try {
        await peerConn.setRemoteDescription(sdp);
        const answer = await peerConn.createAnswer();
        if (answer) {
          sendAnswer(answer);
          console.log(`📤 Sent answer to ${fromUserId}`);
        }
      } catch (err) {
        console.error(`❌ Failed to handle offer from ${fromUserId}:`, err);
      }
    },

    // Received answer
    onAnswer: async (sdp, fromUserId) => {
      console.log(`📩 Received answer from ${fromUserId}`);

      const peerConn = peerConnectionsRef.current.get(fromUserId);
      if (!peerConn) {
        console.error(`❌ No peer connection found for ${fromUserId}`);
        return;
      }

      try {
        await peerConn.setRemoteDescription(sdp);
        console.log(`✅ Set remote description from ${fromUserId}`);
      } catch (err) {
        console.error(
          `❌ Failed to set remote description from ${fromUserId}:`,
          err
        );
      }
    },

    // Received ICE candidate
    onIceCandidate: async (candidate, fromUserId) => {
      console.log(`📩 Received ICE candidate from ${fromUserId}`);

      const peerConn = peerConnectionsRef.current.get(fromUserId);
      if (!peerConn) {
        console.error(`❌ No peer connection found for ${fromUserId}`);
        return;
      }

      try {
        await peerConn.addIceCandidate(candidate);
      } catch (err) {
        console.error(
          `❌ Failed to add ICE candidate from ${fromUserId}:`,
          err
        );
      }
    },
  });

  // Join room
  const joinRoom = useCallback(async () => {
    if (!signalingConnected) {
      console.error('Cannot join room: not connected to signaling server');
      setError(new Error('Not connected to signaling server'));
      return;
    }

    if (!localStream) {
      console.error('Cannot join room: no local stream');
      setError(new Error('Camera or microphone not available'));
      return;
    }

    try {
      setConnectionState('connecting');
      console.log(`🚪 Joining room: ${roomName}`);
      joinSignalingRoom();
      setIsJoined(true);
      setError(null);
    } catch (err) {
      console.error('❌ Failed to join room:', err);
      setError(err as Error);
      setConnectionState('failed');
    }
  }, [signalingConnected, localStream, roomName, joinSignalingRoom]);

  // Leave room
  const leaveRoom = useCallback(() => {
    console.log(`👋 Leaving room: ${roomName}`);

    // Leave signaling room
    leaveSignalingRoom();

    // Close all peer connections
    peerConnectionsRef.current.forEach((peerConn, peerId) => {
      console.log(`🔌 Closing connection to ${peerId}`);
      peerConn.close();
    });
    peerConnectionsRef.current.clear();

    // Clear peers
    setPeers(new Map());
    setIsJoined(false);
    setConnectionState('idle');
  }, [roomName, leaveSignalingRoom]);

  // Screen share
  const startScreenShare = useCallback(async () => {
    try {
      const screenStream = await startScreenShareMedia();

      // Replace video track in all peer connections
      const videoTrack = screenStream.getVideoTracks()[0];

      peerConnectionsRef.current.forEach((peerConn, peerId) => {
        const pc = peerConn.connection;
        const sender = pc.getSenders().find((s) => s.track?.kind === 'video');

        if (sender) {
          console.log(
            `🔄 Replacing video track for ${peerId} with screen share`
          );
          sender.replaceTrack(videoTrack);
        }
      });
    } catch (err) {
      console.error('❌ Failed to start screen share:', err);
      throw err;
    }
  }, [startScreenShareMedia]);

  const stopScreenShare = useCallback(async () => {
    stopScreenShareMedia();

    // Replace back to camera video track
    if (localStream) {
      const videoTrack = localStream.getVideoTracks()[0];

      peerConnectionsRef.current.forEach((peerConn, peerId) => {
        const pc = peerConn.connection;
        const sender = pc.getSenders().find((s) => s.track?.kind === 'video');

        if (sender && videoTrack) {
          console.log(`🔄 Replacing screen share with camera for ${peerId}`);
          sender.replaceTrack(videoTrack);
        }
      });
    }
  }, [localStream, stopScreenShareMedia]);

  // Auto-join if requested
  useEffect(() => {
    if (autoJoin && signalingConnected && localStream && !isJoined) {
      joinRoom();
    }
  }, [autoJoin, signalingConnected, localStream, isJoined, joinRoom]);

  // Update media error
  useEffect(() => {
    if (mediaError) {
      setError(mediaError);
    }
  }, [mediaError]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      leaveRoom();
    };
  }, []); // Empty deps - only on unmount

  return {
    localStream,
    peers,
    isConnected: signalingConnected,
    isJoined,
    connectionState,
    error,
    joinRoom,
    leaveRoom,
    toggleAudio,
    toggleVideo,
    startScreenShare,
    stopScreenShare,
    isAudioEnabled,
    isVideoEnabled,
    isScreenSharing,
  };
}
